// Material-UI Components
import Drawer from "@material-ui/core/Drawer";

// React Hooks and Components
import React, { Fragment, useEffect, useRef, useState } from "react";

// Icons
import binIcon from "../../../assets/icons/bin.svg";
import closeIcon from "../../../assets/icons/close.svg";
import excelDownload from "../../../assets/icons/excelDownload.svg";
import yellowMailingIcon from "../../../assets/icons/mailing_list_icon/yellow-sms-tracking.svg";

// Custom Components
import RequestColorDropdown from "../Dropdown/RequestColorDropdown";
import ExcelUploadPopup from "../Popups/ExcelUploadPopup/ExcelUploadPopup";

// Stylesheets
import "./MailingListDrawer.scss";

// Custom Hooks
import {
  ADD_USERS_AT_ONCE,
  ADD_USERS_AT_ONCE_FOR_INITIAL_STATE,
  ADD_USER_TO_MAILING_LIST,
  REMOVE_USER_FROM_MAILING_LIST,
  UPDATE_MAILING_LIST_COLOR_CODE,
  UPDATE_MAILING_LIST_TITLE,
} from "./hooks/useMailingListForm";

// Utilities
import {
  convertToString,
  deepCopy,
  isEmailValid,
  optionalChaining,
} from "../../../helper";
import { notify } from "../../Notify/Notify";

// Apollo Client
import { gql, useLazyQuery, useMutation } from "@apollo/client";

// Constants
import { colorCodes } from "../constant/common";

// GraphQL Queries
import { GET_ALL_MAILING_LIST } from "../LeftSection/LeftSection";

// External Libraries
import deepEqual from "fast-deep-equal";
import { BootstrapTooltip } from "../Tooltips/BootstrapTooltip";
import { sort } from "fast-sort";
import ConfirmationPopup from "../../../common/components/Popup/ConfirmationPopup";

// Graphql related
const CREATE_DOCUMENT_REQUEST_MAILING_LIST = gql`
  mutation createDocumentRequestMailingList(
    $color: String!
    $mailingListUsers: [DocumentRequestMailingListUserInputType]
    $name: String!
  ) {
    createDocumentRequestMailingList(
      color: $color
      mailingListUsers: $mailingListUsers
      name: $name
    ) {
      success
    }
  }
`;

const DELETE_MAILING_LIST = gql`
  mutation deleteDocumentRequestMailingList($id: String!) {
    deleteDocumentRequestMailingList(id: $id) {
      success
    }
  }
`;

const UPDATE_DOCUMENT_REQUEST_MAILING_LIST = gql`
  mutation UpdateDocumentRequestMailingList(
    $id: String!
    $name: String
    $color: String
    $mailingListUsers: [DocumentRequestMailingListUserInputType]
    $removedMailingListUsers: [String]
  ) {
    updateDocumentRequestMailingList(
      id: $id
      name: $name
      color: $color
      mailingListUsers: $mailingListUsers
      removedMailingListUsers: $removedMailingListUsers
    ) {
      success
    }
  }
`;
export const GET_DOCUMENT_REQUEST_MAILING_LIST_USERS = gql`
  query getDocumentRequestMailingListUsers($mailingListId: String) {
    documentrequestmailinglistusers(mailingListId: $mailingListId) {
      id
      name
      email
    }
  }
`;

function MailingListDrawer({
  isOpen = false,
  onClose = () => console.log("Onclose is missing for TemplateDrawer"),
  isEdit = false,
  mailingListState = {},
  onChange = () =>
    console.error(
      "onChangeForMailingList is not provided to MailingListDrawer"
    ),
}) {
  // Mutations
  const [createMailingList, { loading: isCreateMailingListLoading }] =
    useMutation(CREATE_DOCUMENT_REQUEST_MAILING_LIST, {
      onCompleted: (data) => {
        onClose();
        const title = "Success";
        const detail = `Template ${convertToString(
          mailingListState.mailingListTitle
        )} is successfully added.`;
        const type = "success";
        notify(title, detail, type);
      },
      onError: (error) => {
        const title = "Error";
        const detail = "Something went wrong.";
        const type = "error";
        notify(title, detail, type);
        console.error("Mailing List Mutation Error:", error);
      },
      refetchQueries: [{ query: GET_ALL_MAILING_LIST }],
      awaitRefetchQueries: true,
    });

  const [updateMailingList, { loading: isUpdateMailingListLoading }] =
    useMutation(UPDATE_DOCUMENT_REQUEST_MAILING_LIST, {
      onCompleted: (data) => {
        onClose(isEdit);
        const title = "Success";
        const detail = `Template ${convertToString(
          mailingListState.mailingListTitle
        )} is successfully updated.`;
        const type = "success";
        notify(title, detail, type);
      },
      onError: (error) => {
        const title = "Error";
        const detail = "Something went wrong.";
        const type = "error";
        notify(title, detail, type);
        console.error("Mailing List Mutation Error:", error);
      },
      refetchQueries: [{ query: GET_ALL_MAILING_LIST }],
      awaitRefetchQueries: true,
    });

  const [deleteMailingList, { loading: isDeleteMailingListLoading }] =
    useMutation(DELETE_MAILING_LIST, {
      onCompleted: (data) => {
        onClose(isEdit);
        const title = "Success";
        const detail = `Template ${convertToString(
          mailingListState.mailingListTitle
        )} is successfully deleted.`;
        const type = "success";
        notify(title, detail, type);
      },
      onError: (error) => {
        const title = "Error";
        const detail = "Something went wrong.";
        const type = "error";
        notify(title, detail, type);
        console.error("Delete Mailing List Mutation Error:", error);
      },
      refetchQueries: [{ query: GET_ALL_MAILING_LIST }],
      awaitRefetchQueries: true,
    });

  const [isDeleteConfirmationPopupOpen, setIsDeleteConfirmationPopupOpen] =
    useState(false);

  // Quires
  const [
    getUsers,
    { loading: isUserTableDataGettingLoaded, data: userTableData },
  ] = useLazyQuery(GET_DOCUMENT_REQUEST_MAILING_LIST_USERS, {
    fetchPolicy: "network-only",
  });

  const isCreateBtnDisabled =
    isCreateMailingListLoading || !validateMailingListData(mailingListState);

  const isUpdateBtnDisable =
    isUpdateMailingListLoading ||
    (isEdit &&
      deepEqual(
        getChangesPayload(
          mailingListState.initialMailingListStateForEdit,
          mailingListState
        ),
        {}
      ));

  // Side-effects
  useEffect(() => {
    if (isEdit) {
      getUsers({
        variables: { mailingListId: mailingListState.mailingListId },
      });
    }
  }, [getUsers, mailingListState.mailingListId, isEdit]);

  useEffect(() => {
    if (isEdit && userTableData && !isUserTableDataGettingLoaded) {
      const formattedUsers = extractUserInfo(
        userTableData.documentrequestmailinglistusers
      );
      onChange(ADD_USERS_AT_ONCE, formattedUsers);
      onChange(ADD_USERS_AT_ONCE_FOR_INITIAL_STATE, deepCopy(formattedUsers));
    }
    if (isUserTableDataGettingLoaded) {
      onChange(ADD_USERS_AT_ONCE, []);
      onChange(ADD_USERS_AT_ONCE_FOR_INITIAL_STATE, []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTableData, isEdit, isUserTableDataGettingLoaded]);

  return (
    <Fragment>
      <Drawer open={isOpen}>
        <div className="new-design-v1 mailing-list-drawer-container-wrapper">
          <main className="mailing-list-drawer-container">
            <section className="mailing-list-drawer-header">
              <div className="mailing-list-drawer-header-label">
                {`${isEdit ? "Edit" : "Create"} Mailing List`}
              </div>
              <img
                className="close"
                src={closeIcon}
                alt="closeIcon"
                onClick={() => {
                  onClose(isEdit);
                }}
                width={"24px"}
                height={"24px"}
              />
            </section>
            <section
              className="mailing-list-drawer-form pb-8"
              style={{ maxHeight: "100%", overflowY: "auto" }}
            >
              <div className="mb-5">
                <label
                  htmlFor="mailingListTitle"
                  className="mb-3 mailing-drawer-form-label"
                >
                  Template Title *
                </label>
                <input
                  autoFocus
                  type="text"
                  placeholder="Enter folder name"
                  maxLength="50"
                  id="mailingListTitle"
                  className="mailing-list-drawer-form-input "
                  value={mailingListState.mailingListTitle}
                  onChange={(e) =>
                    onChange(UPDATE_MAILING_LIST_TITLE, e.target.value)
                  }
                />
              </div>
              <div className="mb-8">
                <label className="mb-3 mailing-drawer-form-label">
                  Color Code
                </label>
                <RequestColorDropdown
                  templateColors={colorCodes}
                  onChange={(value) => {
                    onChange(UPDATE_MAILING_LIST_COLOR_CODE, value);
                  }}
                  currentSelectedValue={mailingListState.colorCode}
                />
              </div>
              <div className="recipients-list-wrapper">
                <div className="recipients-list">
                  <img
                    src={yellowMailingIcon}
                    width={"40px"}
                    height={"40px"}
                    alt="mailing"
                  />
                  <div className="recipients-list-header">Recipients List</div>
                </div>
                <div className="recipients-list-sub-header pb-8">
                  {"Use this area to add required recipients."}
                </div>
                <UserForm onChange={onChange} />
                <UsersTable
                  usersData={mailingListState.users}
                  onChange={onChange}
                  isLoading={isUserTableDataGettingLoaded}
                />
              </div>
            </section>
            <section className="px-8">
              {!isEdit && (
                <button
                  className="primary-button"
                  onClick={async () => {
                    const variables =
                      getVariableForCreateMailingList(mailingListState);
                    if (!variables) return;
                    await createMailingList({
                      variables,
                    });
                  }}
                  disabled={isCreateBtnDisabled}
                >
                  Create Mailing List
                </button>
              )}
              {isEdit && (
                <div className="edit-footer-container">
                  <button
                    className="secondary-button"
                    onClick={() => {
                      setIsDeleteConfirmationPopupOpen(true);
                    }}
                  >
                    Delete
                  </button>
                  <button
                    className="primary-button"
                    onClick={async () => {
                      await updateMailingList({
                        variables: {
                          ...getChangesPayload(
                            mailingListState.initialMailingListStateForEdit,
                            mailingListState
                          ),
                        },
                      });
                    }}
                    disabled={isUpdateBtnDisable}
                  >
                    Save Changes
                  </button>
                </div>
              )}
            </section>
          </main>
        </div>
      </Drawer>
      <ConfirmationPopup
        title="Delete Mailing List"
        description={`Are you sure want to delete ${mailingListState.mailingListTitle} ?`}
        open={isDeleteConfirmationPopupOpen}
        confirmationOnClick={async () => {
          await deleteMailingList({
            variables: {
              id: mailingListState.mailingListId,
            },
          });
          setIsDeleteConfirmationPopupOpen(false);
        }}
        isConfirmationBtnDisable={isDeleteMailingListLoading}
        onClose={() => {
          setIsDeleteConfirmationPopupOpen(false);
        }}
      />
    </Fragment>
  );
}

export default MailingListDrawer;

// Helper components
function UserForm({ onChange }) {
  const emailRef = useRef(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isExcelPopupOpened, setExcelPopupOpened] = useState(false);
  const onCloseHandlerForExcelUpload = (
    isForUpload = false,
    usersData = []
  ) => {
    if (isForUpload) {
      onChange(ADD_USERS_AT_ONCE, usersData);
    }
    setExcelPopupOpened(false);
  };
  function isValidInput() {
    // Trim whitespaces and check if the length is greater than two
    const isNameValid = name.trim().length > 1;
    // Return true only if both name and email are valid
    return isNameValid && isEmailValid(email);
  }
  return (
    <div className="new-design-v1 recipients-list-user-form-container">
      <div className="recipients-list-user-card">
        <label
          htmlFor="mailingListUserEmail"
          className="mb-3 mailing-drawer-form-label"
        >
          Email Address
        </label>
        <input
          ref={emailRef}
          type="text"
          placeholder="Enter Email Address"
          maxLength="50"
          id="mailingListUserEmail"
          className="mailing-list-drawer-form-input "
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <label
          htmlFor="mailingListUserName"
          className="mb-3 mt-5 mailing-drawer-form-label"
        >
          Full Name
        </label>
        <input
          type="text"
          placeholder="Enter Full Name"
          maxLength="50"
          id="mailingListUserName"
          className="mailing-list-drawer-form-input "
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="recipients-list-user-form-btns">
        <button
          disabled={!isValidInput()}
          className="light-primary-button mt-5"
          onClick={() => {
            onChange(ADD_USER_TO_MAILING_LIST, { email, name });
            setName("");
            setEmail("");
            if (optionalChaining(emailRef.current, ["focus"], null)) {
              emailRef.current.focus();
            }
          }}
        >
          Add User
        </button>
        <button
          className="secondary-button mt-5"
          onClick={() => setExcelPopupOpened(true)}
        >
          Upload Excel
        </button>
        <ExcelUploadPopup
          open={isExcelPopupOpened}
          handleClose={onCloseHandlerForExcelUpload}
        />
      </div>
    </div>
  );
}

function UsersTable({ usersData = [], onChange, isLoading = false }) {
  const renderCellContent = (value) => {
    if (value?.length > 11) {
      return (
        <BootstrapTooltip title={value} placement="bottom-start">
          <div className="cell-content">{`${value.substring(0, 9)}...`}</div>
        </BootstrapTooltip>
      );
    }
    return <div className="cell-content">{value}</div>;
  };

  const renderUsers = () => {
    return processArray(usersData).map((user) => (
      <div className="table-row" key={user.email}>
        <div className="table-cell">{renderCellContent(user.name)}</div>
        <div className="table-cell">{renderCellContent(user.email)}</div>
        <div className="image-cell">
          <img
            alt="del"
            src={binIcon}
            onClick={() => onChange(REMOVE_USER_FROM_MAILING_LIST, user.email)}
          />
        </div>
      </div>
    ));
  };

  const shouldDisplayMessage = usersData.length > 15;

  return (
    <Fragment>
      <div className="users-table">
        <div className="table-header">
          <div className="table-cell">Name</div>
          <div className="table-cell">Email Address</div>
          <div className="image-cell">
            <img
              alt="del"
              src={excelDownload}
              onClick={() => {
                createExcelFile(usersData);
              }}
            />
          </div>
        </div>
        {!isLoading && <div className="table-row-wrapper">{renderUsers()}</div>}
        {isLoading && (
          <div className="mt-4 d-flex justify-content-center">
            {"User data is loading..."}
          </div>
        )}
      </div>
      {shouldDisplayMessage && (
        <div className="mt-2">
          {"Showing first 10 records and last 5 records."}
        </div>
      )}
    </Fragment>
  );
}

/**
 * Creates an Excel file from the provided data.
 *
 * @param {Array} data - The array of data to be included in the Excel file.
 * @returns {Promise<void>} A Promise that resolves once the Excel file is created.
 */
export const createExcelFile = async (data = []) => {
  const formattedData = data.map((record, index) => ({
    Name: record?.name ?? `no-email${index + 1}@tikler.io`,
    Email: record?.email ?? `Unknown User ${index + 1}`,
  }));
  if (!formattedData || formattedData.length === 0) {
    console.error("Data is empty. Cannot create Excel file.");
    return;
  }

  try {
    const XLSX = await import("xlsx");

    const headers = ["Name", "Email"];
    const sheetName = "Mailing List";

    // Prepare the worksheet data with only 'Name' and 'Email' columns
    const worksheet = XLSX.utils.json_to_sheet(formattedData, {
      header: headers,
    });

    // Create a workbook and add the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Save the workbook to a file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const fileName = "tikler-request-mailing-list-users.xlsx";

    saveAsExcelFile(excelBuffer, fileName);
  } catch (error) {
    console.error("Error creating Excel file:", error.message);

    const title = "Error";
    let detail = "Something went wrong.";

    const specificErrors = [
      "Data is empty. Cannot create Excel file.",
      "Unexpected data format for Excel file creation.",
    ];

    if (specificErrors.includes(error.message)) {
      detail = error.message;
    }

    const type = "error";
    notify(title, detail, type);
  }
};

/**
 * Saves the provided Excel file buffer as a downloadable file with the specified filename.
 *
 * @param {ArrayBuffer} buffer - The Excel file buffer to be saved.
 * @param {string} fileName - The desired filename for the saved Excel file.
 */
const saveAsExcelFile = (buffer, fileName) => {
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};

/**
 * Validates mailing list data to ensure it meets the required criteria.
 *
 * @param {object} mailingListData - The object containing mailing list data.
 * @param {string} mailingListData.mailingListTitle - The title of the mailing list.
 * @param {Array} mailingListData.users - An array of users in the mailing list.
 * @returns {boolean} True if the data is valid, false otherwise.
 */
function validateMailingListData(mailingListData) {
  const { mailingListTitle, users } = mailingListData;

  // Check if mailingListTitle is an empty string or contains only spaces
  if (!mailingListTitle.trim()) {
    return false;
  }

  // Check if the length of the users array is 0
  if (users.length === 0) {
    return false;
  }

  // All conditions passed, return true
  return true;
}

/**
 * Processes an input array based on its length.
 *
 * @param {Array} inputArray - The array to be processed.
 * @returns {Array} A new array containing the first 10 and last 5 elements if the length is more than 15;
 *                  otherwise, returns a new array with the same elements as the input array.
 */
export function processArray(inputArray) {
  // Check if inputArray is an array
  if (!Array.isArray(inputArray)) {
    return [];
  }

  // Handle case when array length is less than or equal to 15
  if (inputArray.length <= 15) {
    return [...inputArray]; // Return a new array to avoid mutating the input
  }

  // Handle case when array length is more than 15
  const first10 = inputArray.slice(0, 10);
  const last5 = inputArray.slice(-5);

  return [...first10, ...last5]; // Concatenate the first 10 and last 5 elements
}
/**
 * Prepares variables for creating a mailing list based on the provided mailing list state.
 *
 * @param {object} mailingListState - The state object representing the mailing list.
 * @param {string} mailingListState.mailingListTitle - The title of the mailing list.
 * @param {object} mailingListState.colorCode - The color code object for the mailing list.
 * @param {string} mailingListState.colorCode.label - The label of the color code.
 * @param {Array} mailingListState.users - An array of users in the mailing list.
 * @param {string} mailingListState.users[].name - The name of the user.
 * @param {string} mailingListState.users[].email - The email of the user.
 * @returns {object} The mutation payload for creating a mailing list, or an object indicating validation errors.
 */
function getVariableForCreateMailingList(mailingListState = {}) {
  const {
    mailingListTitle = "",
    colorCode = {},
    users = [],
  } = mailingListState;

  // Validate required fields
  if (!mailingListTitle || typeof mailingListTitle !== "string") {
    return { one: 1 };
  }

  if (
    !colorCode ||
    typeof colorCode !== "object" ||
    !colorCode.label ||
    typeof colorCode.label !== "string"
  ) {
    return { two: 2 };
  }

  // Construct the mutation payload
  const mutationPayload = {
    color: colorCode.label,
    name: mailingListTitle,
    mailingListUsers: users.map((user) => ({
      name: user.name,
      email: user.email,
    })),
  };
  if (!hasUniqueEmails(mutationPayload.mailingListUsers)) {
    notify("Error", "Duplicate emails found in the recipients list.", "error");
    return false;
  }

  return mutationPayload;
}

/**
 * Extracts user information from the provided data.
 *
 * @param {Array} data - The array of user data to be processed.
 * @returns {Array} An array of objects containing extracted user information with 'id', 'name', and 'email'.
 */
export function extractUserInfo(data) {
  return sort(
    data.map((user) => ({
      id: user.id,
      name: user.name.trim(),
      email: user.email.trim(),
    }))
  ).asc((u) => u.name?.toLowerCase());
}

/**
 * Generates a payload representing the changes between initial and final states of a mailing list.
 *
 * @param {object} initialState - The initial state of the mailing list.
 * @param {string} initialState.colorCode.label - The label of the color code.
 * @param {string} initialState.mailingListTitle - The title of the mailing list.
 * @param {Array} initialState.users - An array of users in the mailing list.
 * @param {string} initialState.users[].email - The email of the user.
 * @param {string} initialState.users[].id - The ID of the user.
 *
 * @param {object} finalState - The final state of the mailing list.
 * @param {string} finalState.colorCode.label - The label of the color code.
 * @param {string} finalState.mailingListTitle - The title of the mailing list.
 * @param {Array} finalState.users - An array of users in the mailing list.
 * @param {string} finalState.users[].email - The email of the user.
 * @param {string} finalState.users[].id - The ID of the user.
 *
 * @returns {object} A payload representing the changes between the initial and final states.
 */
function getChangesPayload(initialState, finalState) {
  if (Object.keys(initialState).length === 0) {
    return {};
  }
  const payload = {};
  // Check if colorCode.label has changed
  if (initialState?.colorCode?.label !== finalState?.colorCode?.label) {
    payload.color = finalState.colorCode.label;
  }

  // Check if mailingListTitle has changed
  if (initialState?.mailingListTitle !== finalState?.mailingListTitle) {
    payload.name = finalState.mailingListTitle;
  }

  // Check if users array has changed
  const addedUsers = finalState.users
    .filter((user) => !initialState.users.some((u) => u.email === user.email))
    .map(({ email = "", name = "" }) => ({ email, name }));
  const removedUsers = initialState?.users.filter(
    (user) => !finalState.users.some((u) => u.email === user.email)
  );

  if (addedUsers.length > 0) {
    payload.mailingListUsers = addedUsers;
  }

  if (removedUsers.length > 0) {
    payload.removedMailingListUsers = removedUsers.map((user) => user.id);
  }

  if (Object.keys(payload).length !== 0) {
    payload.id = finalState.mailingListId;
  }

  return payload;
}

export function hasUniqueEmails(users) {
  const emailSet = new Set();

  for (const user of users) {
    if (emailSet.has(user.email)) {
      return false; // Found a duplicate email
    } else {
      emailSet.add(user.email);
    }
  }

  return true; // All emails are unique
}
